import { createContext, useState, useContext, useCallback, useMemo, useEffect } from "react";

/**
 * BridgeDataContext - Segregated Multi-Bank State Management
 * 
 * This context maintains separate state for each bank account and provides
 * an active bank selection mechanism for secure transaction handling.
 * 
 * Structure:
 * {
 *   kotak: { bank: 'kotak', regId: '...', ... },
 *   axis: { bank: 'axis', regId: '...', ... }
 * }
 */

const BridgeDataContext = createContext();

export const BridgeDataProvider = ({ children }) => {
  // Segregated bank data storage - each bank has its own object
  const [bankDataMap, setBankDataMap] = useState({});
  
  // Active bank identifier (e.g., 'kotak', 'axis')
  const [activeBankKey, setActiveBankKey] = useState(null);
  
  // Initialization state
  const [isInitialized, setIsInitialized] = useState(false);
  const [initializationError, setInitializationError] = useState(null);

  // Keep a global pointer to the active bank for non-React modules/utilities
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.__ACTIVE_BANK_KEY__ = activeBankKey || (process.env.BANK || "");
    }
  }, [activeBankKey]);

  /**
   * Update or add bridge data for a specific bank
   * This is the core function that maintains segregation
   */
  const updateBankData = useCallback((bankKey, bankData) => {
    if (!bankKey) {
      console.error("Cannot update bank data: bankKey is missing");
      return;
    }

    const normalizedKey = bankKey.toLowerCase();
    
    setBankDataMap((prevMap) => ({
      ...prevMap,
      [normalizedKey]: {
        ...(prevMap[normalizedKey] || {}),
        ...bankData,
        bank: normalizedKey,
        lastUpdated: Date.now()
      }
    }));

    console.log(`[BridgeDataContext] Updated data for bank: ${normalizedKey}`);
  }, []);

  /**
   * Process raw bridgeData and segregate it into the appropriate bank slot
   * This function can handle both single and multiple bank data
   */
  const processBridgeData = useCallback((rawData, shouldSwitchActive = true) => {
    if (!rawData || typeof rawData !== 'object') {
      console.error("Invalid bridge data received");
      return false;
    }

    // If rawData contains a 'bank' key, it's single bank data
    if (rawData.bank) {
      const bankKey = rawData.bank.toLowerCase();
      updateBankData(bankKey, rawData);
      
      // Switch to this bank if shouldSwitchActive is true
      // This ensures when new bridgeData arrives, we switch to that bank
      if (shouldSwitchActive) {
        console.log(`[BridgeDataContext] Switching active bank to: ${bankKey}`);
        setActiveBankKey(bankKey);
      } else if (!activeBankKey) {
        // Only set if no active bank exists and not forcing switch
        setActiveBankKey(bankKey);
      }
      return true;
    }

    // If rawData is a map of multiple banks (e.g., from unified storage)
    const keys = Object.keys(rawData);
    if (keys.length === 0) {
      return false;
    }

    keys.forEach((bankKey) => {
      if (rawData[bankKey] && typeof rawData[bankKey] === 'object') {
        updateBankData(bankKey.toLowerCase(), rawData[bankKey]);
      }
    });

    // For multiple banks from cache, only set active if none exists
    // Don't force switch as this is initial load
    if (!activeBankKey && keys.length > 0) {
      setActiveBankKey(keys[0].toLowerCase());
    }

    return true;
  }, [activeBankKey, updateBankData]);

  /**
   * Get the currently active bank's data
   * This is what sensitive modules should use for transactions
   */
  const getActiveBankData = useCallback(() => {
    if (!activeBankKey) {
      return null;
    }
    return bankDataMap[activeBankKey] || null;
  }, [activeBankKey, bankDataMap]);

  /**
   * Get data for a specific bank
   */
  const getBankData = useCallback((bankKey) => {
    if (!bankKey) return null;
    return bankDataMap[bankKey.toLowerCase()] || null;
  }, [bankDataMap]);

  /**
   * Switch to a different bank account
   */
  const switchActiveBank = useCallback((bankKey) => {
    if (!bankKey) {
      console.error("Cannot switch bank: bankKey is missing");
      return false;
    }

    const normalizedKey = bankKey.toLowerCase();
    
    if (!bankDataMap[normalizedKey]) {
      console.error(`Cannot switch to bank: ${normalizedKey} - No data available`);
      return false;
    }

    setActiveBankKey(normalizedKey);
    console.log(`[BridgeDataContext] Switched active bank to: ${normalizedKey}`);
    return true;
  }, [bankDataMap]);

  /**
   * Get list of all available banks
   */
  const availableBanks = useMemo(() => {
    return Object.keys(bankDataMap).map((bankKey) => ({
      key: bankKey,
      name: bankKey.charAt(0).toUpperCase() + bankKey.slice(1),
      data: bankDataMap[bankKey]
    }));
  }, [bankDataMap]);

  /**
   * Check if multiple banks are available
   */
  const hasMultipleBanks = useMemo(() => {
    return availableBanks.length > 1;
  }, [availableBanks]);

  /**
   * Clear all bank data (for logout scenarios)
   */
  const clearAllBankData = useCallback(() => {
    setBankDataMap({});
    setActiveBankKey(null);
    setIsInitialized(false);
    setInitializationError(null);
    console.log("[BridgeDataContext] Cleared all bank data");
  }, []);

  /**
   * Clear data for a specific bank
   */
  const clearBankData = useCallback((bankKey) => {
    if (!bankKey) return;
    
    const normalizedKey = bankKey.toLowerCase();
    
    setBankDataMap((prevMap) => {
      const newMap = { ...prevMap };
      delete newMap[normalizedKey];
      return newMap;
    });

    // If we cleared the active bank, switch to another one
    if (activeBankKey === normalizedKey) {
      const remainingBanks = Object.keys(bankDataMap).filter(
        (key) => key !== normalizedKey
      );
      setActiveBankKey(remainingBanks.length > 0 ? remainingBanks[0] : null);
    }

    console.log(`[BridgeDataContext] Cleared data for bank: ${normalizedKey}`);
  }, [activeBankKey, bankDataMap]);

  /**
   * Mark initialization as complete
   */
  const markInitialized = useCallback((success = true, error = null) => {
    setIsInitialized(success);
    setInitializationError(error);
  }, []);

  const contextValue = {
    // State
    bankDataMap,
    activeBankKey,
    isInitialized,
    initializationError,
    availableBanks,
    hasMultipleBanks,
    
    // Methods
    updateBankData,
    processBridgeData,
    getActiveBankData,
    getBankData,
    switchActiveBank,
    clearAllBankData,
    clearBankData,
    markInitialized
  };

  return (
    <BridgeDataContext.Provider value={contextValue}>
      {children}
    </BridgeDataContext.Provider>
  );
};

/**
 * Hook to access the bridge data context
 */
export const useBridgeData = () => {
  const context = useContext(BridgeDataContext);
  if (!context) {
    throw new Error("useBridgeData must be used within a BridgeDataProvider");
  }
  return context;
};

